import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { Box, Checkbox, CircularProgress, FormControlLabel, Grid, Paper, Theme, Typography } from '@mui/material'
import { format } from 'date-fns'
import { IUserStatisticData } from '../../shared/types/types'
import DropDownComponent, { DropDownElement } from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import DatePickerComponent from '@professocial/professocialFormComponents/FormComponents/DatePickerComponent'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import PageStats from '../../shared/components/ChartComponents/PageStats/PageStats'
import { useDispatch, useSelector } from 'react-redux'
import {
  exportFacebookPageStatisticsForRangeInitiated,
  exportInstagramPageStatisticsForRangeInitiated,
  getEditorFacebookPageStatisticsInitiated,
  getEditorInstagramPageStatisticsInitiated,
} from '../../store/statistics/statistics.editor.actions'
import { GetFacebookPageStatisticsRequest, GetInstagramPageStatisticsRequest, NetworkType } from '@professocialApi/professocial-core-frontend-api'
import { getFBExportedStatistics, getFacebookPageStatistics, getInstagramPageStatistics, getIGExportedStatistics } from '../../store/statistics/statistics.editor.selector'
import { FacebookPageInsights, InstagramPageInsights } from '@professocialApi/professocial-core-frontend-api/models'
import { ChartData } from 'chart.js'
import {
  ExportFacebookPageStatisticsForRangeRequest,
  ExportInstagramPageStatisticsForRangeRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/DefaultApi'
import { saveCSVFromBlob } from '../../util/saveCSVFromBlob'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'
import { PageStatTypes, PageStatTypesObj } from '@professocial/professocialTypes/types/statisticTypes'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'
import { StatisticsRootState } from '../../store/statisticsRoot.reducer'
import { namedRequestInProgress } from '../../store/common/common.selectors'

export const useStyles = makeStyles((theme: Theme) => ({
  row: {
    margin: 0,
    width: '100%',
  },
  row2: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  accordion: {
    border: 0,
    '&:first-child': {
      borderRadius: 0,
    },
    '&:last-child': {
      borderRadius: 0,
    },
    backgroundColor: theme.palette.common.white,
  },
  box: {
    margin: theme.spacing(1),
    width: '100%',
    backgroundColor: theme.palette.common.white,
  },
  paper: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(3),
  },
  boxItem: {
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
    padding: theme.spacing(1),
    minWidth: '255px',
    maxWith: '25%',
  },
  header: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    paddingLeft: 0,
    paddingRight: 0,
  },
  facebookStats: {
    margin: theme.spacing(1),
    width: '100%',
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(3),
  },
  pageParameter: {
    margin: theme.spacing(1),
    width: '100%',
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1),
  },
  pageParamTitle: {
    fontWeight: 'bold',
  },
}))

type PageFilterOptionParam = { key: NetworkType, value: PageStatTypes[] }
type PageFilterChosenOptionParam = { key: NetworkType, value: PageStatTypesObj }

interface PageFilterOptionProps {
  date: DropDownElement[]
  network: DropDownElement[]
  params: PageFilterOptionParam[]
}

interface PageFilterChosenOptionProps {
  date: string
  network: string
  params: PageFilterChosenOptionParam[]
}

type CustomCheckbox = {
  id: string
  type: PageStatTypes
  network: NetworkType
}

const EditorPageStatistics: React.FC<IUserStatisticData> = (props) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation()

  const [dateFrom, setDateFrom] = useState<Date>(new Date(new Date().setDate(new Date().getDate() - 7)))
  const [dateTo, setDateTo] = useState<Date>(new Date())
  // Neuer State für lokales Tracking des Export-Status
  const [localExporting, setLocalExporting] = useState<boolean>(false)

  const dispatchGetFacebookPageStatistics = useCallback((params: GetFacebookPageStatisticsRequest) => dispatch(getEditorFacebookPageStatisticsInitiated(params)), [dispatch])
  const dispatchGetInstagramPageStatistics = useCallback((params: GetInstagramPageStatisticsRequest) => dispatch(getEditorInstagramPageStatisticsInitiated(params)), [dispatch])
  const dispatchGetFBExportedStatistics = useCallback((params: ExportFacebookPageStatisticsForRangeRequest) => dispatch(exportFacebookPageStatisticsForRangeInitiated(params)), [dispatch])
  const dispatchGetIGExportedStatistics = useCallback((params: ExportInstagramPageStatisticsForRangeRequest) => dispatch(exportInstagramPageStatisticsForRangeInitiated(params)), [dispatch])

  const facebookPageStatistics: FacebookPageInsights[] | undefined = useSelector(getFacebookPageStatistics)
  const instagramPageStatistics: InstagramPageInsights[] | undefined = useSelector(getInstagramPageStatistics)
  const exportedFBStatisticsData: Blob | undefined = useSelector(getFBExportedStatistics)
  const exporteIGdStatisticsData: Blob | undefined = useSelector(getIGExportedStatistics)

  const loadFacebookPageStatistics: boolean = useSelector((state: StatisticsRootState) => namedRequestInProgress(state, getEditorFacebookPageStatisticsInitiated.type))
  const loadInstagramPageStatistics: boolean = useSelector((state: StatisticsRootState) => namedRequestInProgress(state, getEditorInstagramPageStatisticsInitiated.type))
  const loadFacebookExport: boolean = useSelector((state: StatisticsRootState) => namedRequestInProgress(state, exportFacebookPageStatisticsForRangeInitiated.type))
  const loadInstagramExport: boolean = useSelector((state: StatisticsRootState) => namedRequestInProgress(state, exportInstagramPageStatisticsForRangeInitiated.type))
  // Kombinieren des Redux-States mit lokalem State
  const isExporting: boolean = loadFacebookExport || loadInstagramExport || localExporting

  console.log('loadFacebookPageStatistics', loadFacebookPageStatistics)
  console.log('loadInstagramPageStatistics', loadInstagramPageStatistics)
  console.log('loadFacebookExport', loadFacebookExport)
  console.log('loadInstagramExport', loadInstagramExport)
  console.log('isExporting', isExporting)
  console.log('localExporting', localExporting)

  const filterOptions: PageFilterOptionProps = {
    date: [
      { key: '7', value: t('statisticsCommon:last7Days') },
      { key: '30', value: t('statisticsCommon:last30Days') },
      { key: '0', value: t('statisticsCommon:individual') },
    ],
    network: [
      { key: NetworkType.Facebook, value: t('statisticsCommon:facebookNetwork') },
      { key: NetworkType.Instagram, value: t('statisticsCommon:instagramNetwork') },
    ],
    params: [
      { key: NetworkType.Facebook, value: [PageStatTypes.fans, PageStatTypes.dailyReach, PageStatTypes.pageCalls, PageStatTypes.talkingAboutCount, PageStatTypes.organicReach, PageStatTypes.paidReach] },
      { key: NetworkType.Instagram, value: [PageStatTypes.followersCount, PageStatTypes.followsCount, PageStatTypes.mediaCount, PageStatTypes.websiteClicks, PageStatTypes.emailContacts, PageStatTypes.profileViews] },
    ],
  }

  const [chosenFilterOption, setChosenFilterOption] = useState<PageFilterChosenOptionProps>(
    {
      date: '7',
      network: NetworkType.Facebook,
      params: [
        { key: NetworkType.Facebook, value: { fans: true, dailyReach: true, pageCalls: true, talkingAboutCount: true, organicReach: true, paidReach: true } },
        { key: NetworkType.Instagram, value: { followersCount: true, followsCount: true, mediaCount: false, websiteClicks: true, emailContacts: true, profileViews: true } },
      ],
    })

  const checkboxes: CustomCheckbox[] = [
    { id: 'fans', type: PageStatTypes.fans, network: NetworkType.Facebook },
    { id: 'dailyReach', type: PageStatTypes.dailyReach, network: NetworkType.Facebook },
    { id: 'pageCalls', type: PageStatTypes.pageCalls, network: NetworkType.Facebook },
    { id: 'talkingAboutCount', type: PageStatTypes.talkingAboutCount, network: NetworkType.Facebook },
    { id: 'organicReach', type: PageStatTypes.organicReach, network: NetworkType.Facebook },
    { id: 'paidReach', type: PageStatTypes.paidReach, network: NetworkType.Facebook },
    { id: 'followersCount', type: PageStatTypes.followersCount, network: NetworkType.Instagram },
    { id: 'followsCount', type: PageStatTypes.followsCount, network: NetworkType.Instagram },
    { id: 'mediaCount', type: PageStatTypes.mediaCount, network: NetworkType.Instagram },
    { id: 'emailContacts', type: PageStatTypes.emailContacts, network: NetworkType.Instagram },
    { id: 'websiteClicks', type: PageStatTypes.websiteClicks, network: NetworkType.Instagram },
    { id: 'profileViews', type: PageStatTypes.profileViews, network: NetworkType.Instagram },
  ]

  const handleDateChange = (date: Date, setToFullDay: boolean, setterFunction: (date: Date) => void) => {
    setToFullDay
      ? date.setHours(23, 59, 59, 0)
      : date.setHours(1, 0, 0, 0)

    setterFunction(date)
  }

  const getStatisticsData = (network: string = NetworkType.Facebook) => {
    if (network === NetworkType.Facebook) {
      const facebookParams: GetFacebookPageStatisticsRequest = {
        from: dateFrom,
        to: dateTo,
      }

      dispatchGetFacebookPageStatistics(facebookParams)
    }

    if (network === NetworkType.Instagram) {
      const instagramParams: GetInstagramPageStatisticsRequest = {
        from: dateFrom,
        to: dateTo,
      }

      dispatchGetInstagramPageStatistics(instagramParams)
    }
  }

  const mapFacebookPageStatisticsToDataset = (data: FacebookPageInsights[]): ChartData => {
    const params = chosenFilterOption.params.filter((param) => param.key === NetworkType.Facebook)[0]
    return {
      datasets: Object.keys(params.value)
        .filter(param => params.value[param as keyof PageStatTypesObj])
        .map((param) => {
          return {
            label: t(`statisticsCommon:${param}`),
            data: data.map(item => item[param as keyof FacebookPageInsights] as number),
          }
        }),
      labels: data.map(item => format(item.date, 'dd.MM.yyyy').toString()),
    }
  }

  const mapInstagramPageStatisticsToDataset = (data: InstagramPageInsights[]): ChartData => {
    const params = chosenFilterOption.params.filter((param) => param.key === NetworkType.Instagram)[0]
    return {
      datasets: Object.keys(params.value)
        .filter(param => params.value[param as keyof PageStatTypesObj])
        .map((param) => {
          return {
            label: t(`statisticsCommon:${param}`),
            data: data.map(item => item[param as keyof InstagramPageInsights] as number),
          }
        }),
      labels: data.map(item => format(item.date, 'dd.MM.yyyy').toString()),
    }
  }

  useEffect(() => {
    getStatisticsData()
  }, [])

  useEffect(() => {
    getStatisticsData(chosenFilterOption.network)
  }, [dateFrom, dateTo, chosenFilterOption.network])

  useEffect(() => {
    const days = parseInt(chosenFilterOption.date)

    if (days) {
      setDateTo(new Date())
      setDateFrom(new Date(new Date().setDate(new Date().getDate() - days)))
    }
  }, [chosenFilterOption.date])

  useEffect(() => {
    if (exportedFBStatisticsData) {
      saveCSVFromBlob(exportedFBStatisticsData, dateFrom, dateTo)
      setLocalExporting(false) // Reset lokalen Export-Status
    }
  }, [exportedFBStatisticsData])

  useEffect(() => {
    if (exporteIGdStatisticsData) {
      saveCSVFromBlob(exporteIGdStatisticsData, dateFrom, dateTo)
      setLocalExporting(false) // Reset lokalen Export-Status
    }
  }, [exporteIGdStatisticsData])

  const paramChanged = (event: React.ChangeEvent<HTMLInputElement>, networkType: NetworkType = NetworkType.Facebook) => {
    const changedType = event.target.id as PageStatTypes

    setChosenFilterOption({
      ...chosenFilterOption,
      params: chosenFilterOption.params
        .map((param) => param.key === networkType
          ? { ...param, value: { ...param.value, [changedType]: event.target.checked } }
          : param),
    })
  }

  const csvExport = () => {
    setLocalExporting(true) // Setzen des lokalen Export-Status

    if (chosenFilterOption.network === NetworkType.Facebook) {
      dispatchGetFBExportedStatistics({
        from: dateFrom,
        to: dateTo,
      })
    } else if (chosenFilterOption.network === NetworkType.Instagram) {
      dispatchGetIGExportedStatistics({
        from: dateFrom,
        to: dateTo,
      })
    }
  }

  const getCheckboxList = (network: string) => checkboxes.filter(checkbox => checkbox.network === network as NetworkType)

  const renderFacebookPageStats = (
    statistics: FacebookPageInsights[],
    mapFunction: (data: FacebookPageInsights[]) => ChartData
  ) => {
    return statistics.length > 0 ? (
      <PageStats colors={props.colors} data={mapFunction(statistics)} />
    ) : (
      <NoDataComponent noDataText={t('statisticsCommon:noDataYet')} alignment="center" />
    )
  }

  const renderInstagramPageStats = (
    statistics: InstagramPageInsights[],
    mapFunction: (data: InstagramPageInsights[]) => ChartData
  ) => {
    return statistics.length > 0 ? (
      <PageStats colors={props.colors} data={mapFunction(statistics)} />
    ) : (
      <NoDataComponent noDataText={t('statisticsCommon:noDataYet')} alignment="center" />
    )
  }

  const renderContent = () => {
    if (chosenFilterOption.network === NetworkType.Facebook) {
      return (facebookPageStatistics && !loadFacebookPageStatistics)
        ? renderFacebookPageStats(facebookPageStatistics, mapFacebookPageStatisticsToDataset)
        : <Preloader />
    } else if (chosenFilterOption.network === NetworkType.Instagram) {
      return (instagramPageStatistics && !loadInstagramPageStatistics)
        ? renderInstagramPageStats(instagramPageStatistics, mapInstagramPageStatisticsToDataset)
        : <Preloader />
    }
  }

  return (
    <Grid container>
      <Grid container direction='row'>
        <Grid container item xs={12}>
          <h3>{t('statisticsCommon:statistics')}</h3>
        </Grid>
      </Grid>
      <Grid container item direction='row' sm={12} className={classes.row}>
        <Grid container item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Box className={classes.boxItem}>
              <DropDownComponent
                options={filterOptions.network}
                label={t('statisticsCommon:network')}
                value={chosenFilterOption.network}
                size='small'
                onChange={option => setChosenFilterOption({ ...chosenFilterOption, network: option.key })}
                disableClearable
              />
            </Box>
            <Box className={classes.boxItem}>
              <DropDownComponent
                options={filterOptions.date}
                label={t('statisticsCommon:dateRange')}
                value={chosenFilterOption.date}
                size='small'
                onChange={option => setChosenFilterOption({ ...chosenFilterOption, date: option.key })}
                disableClearable
              />
            </Box>
            <Box className={classes.boxItem}>
              <DatePickerComponent
                label={t('statisticsCommon:dateRangeFrom')}
                value={dateFrom}
                size='small'
                inputVariant='outlined'
                onChangeDate={date => date && handleDateChange(date, false, setDateFrom)}
                disabled={parseInt(chosenFilterOption.date) !== 0}
                maxDate={new Date()}
              />
            </Box>
            <Box className={classes.boxItem}>
              <DatePickerComponent
                label={t('statisticsCommon:dateRangeTo')}
                value={dateTo}
                inputVariant='outlined'
                size='small'
                onChangeDate={date => date && handleDateChange(date, true, setDateTo)}
                disabled={parseInt(chosenFilterOption.date) !== 0}
                minDate={dateFrom}
                maxDate={new Date()}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <Grid container item direction='row' sm={12} className={classes.row}>
        <Grid container item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Box alignSelf='center' sx={{ p: 1 }}>
              <Typography className={classes.pageParamTitle}>{t('statisticsCommon:pageParameters')}&nbsp;</Typography>
            </Box>
            {getCheckboxList(chosenFilterOption.network)
              .map((checkbox, index) => (
                <Box key={checkbox.id} flexGrow={index + 1 === getCheckboxList(checkbox.network).length ? 1 : 0}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id={checkbox.id}
                        color='primary'
                        checked={!!chosenFilterOption.params
                          .find(param => param.key === checkbox.network)?.value[checkbox.type]}
                        onChange={(event) => paramChanged(event, checkbox.network)}
                      />
                    }
                    label={t(`statisticsCommon:${checkbox.id}`) as string} labelPlacement='end'
                  />
                </Box>
              ))}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ButtonComponent
                color='secondary'
                variant='contained'
                text={t('account:csvExport')}
                aria-label='Download'
                startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : 'input'}
                disableElevation
                onClick={csvExport}
                disabled={isExporting}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <Grid container direction='row' className={classes.row}>
        <Grid container item xs={12} md={12}>
          <Paper className={classes.paper}>
            {renderContent()}
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  )
}
export default EditorPageStatistics
